export interface Application {
  id: string;
  name: string;
  icon: string;
  description?: string;
  version: string;
  size: string;
  releaseDate: string;
  source: AppSource;
  platform: AppPlatform;
  bundleId?: string;
  categories?: string[];
  customers: string[];
  status: 'published' | 'draft' | 'pending';
  pppcSettings?: PPPCSettings;
  // New fields for ABM and App Store support
  abmConnectionId?: string;
  appStoreId?: string;
  appStoreUrl?: string;
  licenseTotal?: number;
  licenseUsed?: number;
  countryCode?: string;
  price?: number;
  minimumOsVersion?: string;
  developer?: string;
  rating?: number;
  /** Org-unit display names contributing licenses (for `source === 'abm'`). */
  abmOrgUnits?: string[];
}

export type AppSource = 'appStore' | 'abm' | 'custom' | 'macOSCatalog' | 'private';
export type AppPlatform = 'ios' | 'ipados' | 'macos' | 'tvos';

export interface AppStore {
  id: string;
  name: string;
  icon: string;
}

// App Store search result from iTunes API
export interface AppStoreSearchResult {
  appStoreId: string;
  name: string;
  bundleId: string;
  icon: string;
  iconLarge: string;
  version: string;
  description: string;
  releaseDate: string;
  size: string;
  minimumOsVersion: string;
  price: number;
  formattedPrice: string;
  rating: number;
  ratingCount: number;
  appStoreUrl: string;
  developer: string;
  category: string;
  categories: string[];
  platform: AppPlatform;
}

// ABM App from Apps & Books
export interface ABMApp {
  id: string;
  name: string;
  bundleId: string;
  icon: string;
  version: string;
  licenseTotal: number;
  licenseUsed: number;
  abmConnectionId: string;
  abmConnectionName: string;
  platform: AppPlatform;
}

export interface PPPCSettings {
  codeRequirement?: string;
  identifierType?: 'bundleID' | 'path';
  privacyPermissions?: PPPCPermissions[];
}

export interface PPPCPermissions {
  serviceIdentifier: PPPCService;
  allowed: boolean;
  codeRequirementOverride?: string;
}

export type PPPCService = 
  | 'addressBook'
  | 'calendar'
  | 'reminders'
  | 'photos'
  | 'camera'
  | 'microphone'
  | 'accessibility'
  | 'postEvent'
  | 'systemPolicyAllFiles'
  | 'filesAndFolders'
  | 'screenCapture'
  | 'bluetooth'
  | 'location'
  | 'systemPolicyNetworkVolumes'
  | 'systemPolicyRemovableVolumes'
  | 'systemPolicySysAdminFiles';

export const APP_PLATFORMS = ['ios', 'ipados', 'macos', 'tvos'];
export const APP_CATEGORIES = [
  'Productivity',
  'Business',
  'Education',
  'Utilities',
  'Entertainment',
  'Social',
  'Graphics & Design',
  'Developer Tools',
  'Medical'
];

export const PPPC_SERVICE_LABELS: Record<PPPCService, string> = {
  addressBook: 'Contacts',
  calendar: 'Calendar',
  reminders: 'Reminders',
  photos: 'Photos',
  camera: 'Camera',
  microphone: 'Microphone',
  accessibility: 'Accessibility',
  postEvent: 'Apple Events',
  systemPolicyAllFiles: 'Full Disk Access',
  filesAndFolders: 'Files and Folders',
  screenCapture: 'Screen Recording',
  bluetooth: 'Bluetooth',
  location: 'Location',
  systemPolicyNetworkVolumes: 'Network Volumes',
  systemPolicyRemovableVolumes: 'Removable Volumes',
  systemPolicySysAdminFiles: 'System Administration Files'
};

// App Configuration Types
export interface AppConfigKey {
  key: string;
  value: string | boolean | number | string[];
  type: 'string' | 'boolean' | 'number' | 'array';
  description?: string;
  required?: boolean;
}

export interface AppConfigurationData {
  keys: AppConfigKey[];
}

export interface AppConfiguration {
  id: string;
  applicationId: string;
  name: string;
  description?: string;
  configurationData: AppConfigurationData;
  customers: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  currentVersion?: number;
}

export interface AppConfigurationVersion {
  id: string;
  configurationId: string;
  versionNumber: number;
  name: string;
  description?: string;
  configurationData: AppConfigurationData;
  customers: string[];
  isActive: boolean;
  changeSummary?: string;
  changeDetails?: VersionChangeDetails;
  createdBy?: string;
  createdAt: string;
  versionLabel?: string;
  isMilestone: boolean;
}

export interface VersionChangeDetails {
  changes: VersionChange[];
  summary: string;
}

export interface VersionChange {
  field: 'name' | 'description' | 'keys' | 'customers' | 'isActive';
  action?: 'added' | 'modified' | 'removed';
  key?: string;
  oldValue?: any;
  newValue?: any;
  value?: any;
}

export interface VersionMetrics {
  date: string;
  versionNumber: number;
  keysAdded: number;
  keysModified: number;
  keysRemoved: number;
  totalKeys: number;
  customersAdded: number;
  customersRemoved: number;
  totalCustomers: number;
  activationChanged: boolean;
  isActive: boolean;
  isMilestone: boolean;
  changeSummary: string;
  versionId: string;
}

export interface TimelineFilters {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  changeTypes: {
    keyChanges: boolean;
    customerChanges: boolean;
    activationChanges: boolean;
    nameChanges: boolean;
  };
  showMilestonesOnly: boolean;
}

